/**
 * The command journal and the snapshot handle used for kernel crash recovery
 * (Requirement 16.2; the design's "Worker crash recovery" note).
 *
 * The design pairs a snapshot with an append-only command journal: on worker
 * termination the main thread restarts the worker, reloads the last snapshot, and
 * replays the journal recorded since that snapshot. Task 23 owns the durable half
 * of that pair — the IndexedDB snapshot blob and the persisted journal that also
 * serve offline editing (Requirements 14.4–14.5). What lives here is the shape of
 * both halves plus an in-memory journal implementation, so recovery is real and
 * testable today rather than a stub waiting on persistence.
 *
 * **Why the journal records operations, not commands.** A journal of commands
 * alone replays wrong: `dispatch A`, `dispatch B`, `undo` must not replay as
 * `A, B`. So the unit is a {@link JournalOp} — a command, a batch, an undo, or a
 * redo — which is exactly the sequence of things the kernel was asked to do. That
 * keeps replay a straight fold over the log with no interpretation.
 *
 * **Only successful operations are journaled.** A rejected command changes
 * nothing (validation precedes mutation in the dispatcher), so replaying it would
 * at best be a no-op and at worst re-run a failure. The client appends after it
 * sees `ok: true`.
 *
 * Every {@link JournalOp} is JSON-serializable, because {@link Command} is: the
 * same entries that replay into a restarted worker are what task 23 will append to
 * IndexedDB and upload on reconnect.
 */

import type { Command } from '../core/command/index.js';

/** One thing the kernel was asked to do, in the order it was asked. */
export type JournalOp =
  | { k: 'command'; command: Command }
  | { k: 'batch'; commands: Command[]; label: string }
  | { k: 'undo' }
  | { k: 'redo' };

/** A journal entry: an operation plus its position and wall-clock time. */
export interface JournalEntry {
  /** 1-based, strictly increasing, gap-free within a session. */
  seq: number;
  at: number;
  op: JournalOp;
}

/**
 * An append-only log of kernel operations.
 *
 * The in-memory implementation lives here. The durable variant — backed by an
 * IndexedDB object store (`src/io/durable.ts`) — implements the same interface
 * and adds the two optional lifecycle methods, so the recovery code can wait for
 * the log to hydrate and flush without knowing which kind it holds.
 */
export interface CommandJournal {
  /** Appends `op` and returns the entry that was written. */
  append(op: JournalOp): JournalEntry;
  /** Entries with `seq > afterSeq`, in order. The replay input. */
  since(afterSeq: number): JournalEntry[];
  /** Every retained entry, in order. */
  entries(): JournalEntry[];
  /** Sequence number of the newest entry, or 0 when the journal is empty. */
  head(): number;
  /** Drops entries at or below `throughSeq`, which a snapshot has absorbed. */
  truncate(throughSeq: number): void;
  size(): number;
  /**
   * Hydrates a durable journal from storage before replay. Absent on the
   * in-memory journal, where there is nothing to hydrate.
   */
  load?(): Promise<void>;
  /**
   * Awaits any pending durable writes. Absent on the in-memory journal, whose
   * writes are already synchronous.
   */
  flush?(): Promise<void>;
}

export interface MemoryJournalOptions {
  /**
   * Entries retained before the oldest is dropped. A bound is required: a session
   * that edits for hours must not grow the log without limit. Dropping an entry
   * makes recovery from before it impossible, which is precisely why task 23's
   * snapshots exist — a snapshot lets {@link CommandJournal.truncate} keep the log
   * short without losing state.
   */
  capacity?: number;
  now?: () => number;
}

/** Entries an in-memory journal keeps by default. */
export const JOURNAL_CAPACITY = 5_000;

/** An in-memory {@link CommandJournal}. Durable variant lands with task 23. */
export function createMemoryJournal(opts: MemoryJournalOptions = {}): CommandJournal {
  const capacity = opts.capacity ?? JOURNAL_CAPACITY;
  if (!Number.isInteger(capacity) || capacity < 1) {
    throw new RangeError(`journal capacity must be a positive integer: ${capacity}`);
  }
  const now = opts.now ?? Date.now;
  const log: JournalEntry[] = [];
  let nextSeq = 1;

  return {
    append(op: JournalOp): JournalEntry {
      const entry: JournalEntry = { seq: nextSeq++, at: now(), op };
      log.push(entry);
      while (log.length > capacity) log.shift();
      return entry;
    },
    since(afterSeq: number): JournalEntry[] {
      return log.filter((e) => e.seq > afterSeq);
    },
    entries(): JournalEntry[] {
      return [...log];
    },
    head(): number {
      return nextSeq - 1;
    },
    truncate(throughSeq: number): void {
      while (log.length > 0 && (log[0] as JournalEntry).seq <= throughSeq) log.shift();
    },
    size(): number {
      return log.length;
    },
  };
}

/**
 * A workbook snapshot, opaque above the Persistence_Service.
 *
 * `seq` is the journal position the snapshot was taken at: recovery restores the
 * payload and then replays `journal.since(snapshot.seq)`. The payload stays
 * `unknown` because its encoding is task 23's `.wbk` container, and the kernel
 * bridge must not grow an opinion about it.
 */
export interface KernelSnapshot {
  seq: number;
  payload: unknown;
}

/**
 * Where snapshots come from during recovery.
 *
 * Task 23 implements this over IndexedDB. {@link createNullSnapshotStore} is the
 * honest placeholder until then: it holds nothing, so recovery replays the whole
 * retained journal from an empty workbook — correct, just less efficient than it
 * will be once snapshots exist.
 */
export interface SnapshotStore {
  latest(): Promise<KernelSnapshot | null>;
  put(snapshot: KernelSnapshot): Promise<void>;
}

/** A snapshot store that never has a snapshot. Replaced by task 23. */
export function createNullSnapshotStore(): SnapshotStore {
  return {
    latest(): Promise<KernelSnapshot | null> {
      return Promise.resolve(null);
    },
    put(): Promise<void> {
      return Promise.resolve();
    },
  };
}
